import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '@/shared/store/store';
import { Module } from '../types/module.types';

// Selectores básicos
export const selectModulesState = (state: RootState) => state.modules;

export const selectModules = (state: RootState) => state.modules.modules;
export const selectCategories = (state: RootState) => state.modules.categories;
export const selectCurrentModule = (state: RootState) =>
  state.modules.currentModule;
export const selectCurrentCategory = (state: RootState) =>
  state.modules.currentCategory;
export const selectLoading = (state: RootState) => state.modules.loading;
export const selectError = (state: RootState) => state.modules.error;
export const selectPagination = (state: RootState) => state.modules.pagination;
export const selectFilters = (state: RootState) => state.modules.filters;

// Selectores computados
export const selectModulesByCategory = createSelector(
  [selectModules, selectCategories],
  (modules, categories) => {
    // Asegurar que categories sea un array
    const categoriesArray = Array.isArray(categories) ? categories : [];
    return categoriesArray.map(category => ({
      ...category,
      modules: modules.filter(module => module.categoryId === category.id),
    }));
  }
);

export const selectActiveModules = createSelector([selectModules], modules =>
  modules.filter(module => module.status === 'active')
);

export const selectInactiveModules = createSelector([selectModules], modules =>
  modules.filter(module => module.status === 'inactive')
);

export const selectPublicModules = createSelector([selectModules], modules =>
  modules.filter(module => module.visibility === 'public')
);

export const selectInternalModules = createSelector([selectModules], modules =>
  modules.filter(module => module.visibility === 'internal')
);

export const selectBetaModules = createSelector([selectModules], modules =>
  modules.filter(module => module.visibility === 'beta')
);

export const selectRequiredModules = createSelector([selectModules], modules =>
  modules.filter(module => module.isRequired)
);

export const selectAddonModules = createSelector([selectModules], modules =>
  modules.filter(module => module.isAddon)
);

export const selectModulesByStatus = createSelector(
  [selectModules],
  modules => {
    return modules.reduce(
      (acc, module) => {
        if (!acc[module.status]) {
          acc[module.status] = [];
        }
        acc[module.status].push(module);
        return acc;
      },
      {} as Record<string, Module[]>
    );
  }
);

export const selectModulesByVisibility = createSelector(
  [selectModules],
  modules => {
    return modules.reduce(
      (acc, module) => {
        if (!acc[module.visibility]) {
          acc[module.visibility] = [];
        }
        acc[module.visibility].push(module);
        return acc;
      },
      {} as Record<string, Module[]>
    );
  }
);

export const selectModulesByCategoryId = createSelector(
  [selectModules, (_state: RootState, categoryId: string) => categoryId],
  (modules, categoryId) =>
    modules.filter(module => module.categoryId === categoryId)
);

export const selectModuleById = createSelector(
  [selectModules, (_state: RootState, id: string) => id],
  (modules, id) => modules.find(module => module.id === id)
);

export const selectCategoryById = createSelector(
  [selectCategories, (_state: RootState, id: string) => id],
  (categories, id) => {
    // Asegurar que categories sea un array
    const categoriesArray = Array.isArray(categories) ? categories : [];
    return categoriesArray.find(category => category.id === id);
  }
);

export const selectFilteredModules = createSelector(
  [selectModules, selectFilters],
  (modules, filters) => {
    let filtered = [...modules];

    // Filtrar por búsqueda
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        module =>
          module.name.toLowerCase().includes(searchLower) ||
          module.description.toLowerCase().includes(searchLower)
      );
    }

    // Filtrar por categoría
    if (filters.categoryId) {
      filtered = filtered.filter(
        module => module.categoryId === filters.categoryId
      );
    }

    // Filtrar por estado
    if (filters.status) {
      filtered = filtered.filter(module => module.status === filters.status);
    }

    // Filtrar por visibilidad
    if (filters.visibility) {
      filtered = filtered.filter(
        module => module.visibility === filters.visibility
      );
    }

    // Filtrar por requerido
    if (filters.isRequired !== null) {
      filtered = filtered.filter(
        module => module.isRequired === filters.isRequired
      );
    }

    // Filtrar por add-on
    if (filters.isAddon !== null) {
      filtered = filtered.filter(module => module.isAddon === filters.isAddon);
    }

    return filtered;
  }
);

export const selectModulesStats = createSelector(
  [selectModules, selectCategories],
  (modules, categories) => {
    const totalModules = modules.length;
    const activeModules = modules.filter(m => m.status === 'active').length;
    const inactiveModules = modules.filter(m => m.status === 'inactive').length;
    const publicModules = modules.filter(m => m.visibility === 'public').length;
    const internalModules = modules.filter(
      m => m.visibility === 'internal'
    ).length;
    const betaModules = modules.filter(m => m.visibility === 'beta').length;
    const requiredModules = modules.filter(m => m.isRequired).length;
    const addonModules = modules.filter(m => m.isAddon).length;

    // Asegurar que categories sea un array
    const categoriesArray = Array.isArray(categories) ? categories : [];
    const modulesByCategory = categoriesArray.map(category => ({
      category: category.name,
      count: modules.filter(m => m.categoryId === category.id).length,
    }));

    return {
      totalModules,
      activeModules,
      inactiveModules,
      publicModules,
      internalModules,
      betaModules,
      requiredModules,
      addonModules,
      modulesByCategory,
    };
  }
);

export const selectModulesWithCategories = createSelector(
  [selectModules, selectCategories],
  (modules, categories) => {
    // Asegurar que categories sea un array
    const categoriesArray = Array.isArray(categories) ? categories : [];
    return modules.map(module => {
      const category = categoriesArray.find(cat => cat.id === module.categoryId);
      return {
        ...module,
        category,
      };
    });
  }
);

export const selectModulesByFeature = createSelector(
  [selectModules],
  modules => {
    const featureMap = new Map<string, Module[]>();

    modules.forEach(module => {
      module.features.forEach(feature => {
        if (!featureMap.has(feature)) {
          featureMap.set(feature, []);
        }
        featureMap.get(feature)!.push(module);
      });
    });

    return Array.from(featureMap.entries()).map(([feature, modules]) => ({
      feature,
      modules,
      count: modules.length,
    }));
  }
);

export const selectModulesByVersion = createSelector(
  [selectModules],
  modules => {
    const versionMap = new Map<string, Module[]>();

    modules.forEach(module => {
      if (!versionMap.has(module.version)) {
        versionMap.set(module.version, []);
      }
      versionMap.get(module.version)!.push(module);
    });

    return Array.from(versionMap.entries()).map(([version, modules]) => ({
      version,
      modules,
      count: modules.length,
    }));
  }
);

export const selectModulesSearchResults = createSelector(
  [selectModules, (_state: RootState, searchTerm: string) => searchTerm],
  (modules, searchTerm) => {
    if (!searchTerm) return [];

    const searchLower = searchTerm.toLowerCase();
    return modules.filter(
      module =>
        module.name.toLowerCase().includes(searchLower) ||
        module.description.toLowerCase().includes(searchLower) ||
        module.features.some(feature =>
          feature.toLowerCase().includes(searchLower)
        )
    );
  }
);

export const selectModulesPagination = createSelector(
  [selectFilteredModules, selectPagination],
  (filteredModules, pagination) => {
    const totalPages = Math.ceil(filteredModules.length / pagination.limit);
    const startIndex = (pagination.page - 1) * pagination.limit;
    const endIndex = startIndex + pagination.limit;
    const paginatedModules = filteredModules.slice(startIndex, endIndex);

    return {
      ...pagination,
      total: filteredModules.length,
      totalPages,
      hasNextPage: pagination.page < totalPages,
      hasPreviousPage: pagination.page > 1,
      paginatedModules,
    };
  }
);
